/**
 * Icon picker, over the shared catalogue.
 *
 * The catalogue is in the hundreds, so the search field takes focus on open and the list is
 * capped — the footer says how much of the catalogue is showing so the cap is not mistaken for
 * "that's all there is".
 *
 * A popover, positioned by whoever opens it: the anchor needs `position: relative`.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { IGLIcon } from '@group-link-one/gl-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import {
  countIconMatches,
  getIconInitials,
  isKnownIcon,
  searchIcons,
} from '@/Shared/icons/iconCatalog';

const popoverStyle = css`
  position: absolute;
  z-index: var(--z-drawer);
  top: calc(100% + 6px);
  left: 0;
  width: min(320px, 90vw);
  background: var(--color-surface);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-m);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
`;

const searchWrapStyle = css`
  padding: var(--space-s);
  border-bottom: 1px solid var(--color-divider);
`;

const searchStyle = css`
  width: 100%;
  height: 34px;
  padding: 0 var(--space-s);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-control);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  background: var(--color-surface);
  color: var(--color-text);

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const listStyle = css`
  max-height: 260px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 2px;
  padding: var(--space-s);
`;

const optionStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: var(--space-s) 4px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: transparent;
  cursor: pointer;
  font-family: var(--font-sans);

  &:hover {
    background: var(--color-surface-hover);
  }
`;

const optionSelectedStyle = css`
  border-color: var(--color-primary);
  background: var(--color-primary-subtle);
`;

const optionLabelStyle = css`
  font-size: 10px;
  color: var(--color-text-secondary);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const initialsStyle = css`
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: var(--font-weight-bold);
`;

const footerStyle = css`
  padding: var(--space-s) var(--space-m);
  border-top: 1px solid var(--color-divider);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const emptyStyle = css`
  padding: var(--space-m);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

/**
 * Renders the glyph, or its capitals when this build has no icon by that name.
 *
 * The name is checked against the catalogue **before** rendering. `GLIcon` does not throw on an
 * unknown variant — it renders the words "Icon not found: Map.Layers" — so a try/catch around it
 * never fired, and a pasted JSON with a wrong icon printed that sentence across the card.
 */
export const IconGlyph = ({
  value,
  size,
  color,
}: {
  value: string;
  size: number;
  color: string;
}): JSX.Element => {
  if (!isKnownIcon(value)) {
    return (
      <span className={initialsStyle} style={{ color, fontSize: Math.max(10, size - 8) }}>
        {getIconInitials(value)}
      </span>
    );
  }

  return <GLIcon variant={value.trim() as IGLIcon['variant']} size={size} fill={color} />;
};

export interface IconPickerProps {
  value: string;
  onSelect: (value: string) => void;
  onClose: () => void;
  color: string;
}

export const IconPicker = ({ value, onSelect, onClose, color }: IconPickerProps): JSX.Element => {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // The point of opening the picker is to search, so the field takes focus.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const results = useMemo(() => searchIcons(query), [query]);
  const total = useMemo(() => countIconMatches(query), [query]);

  return (
    <div ref={popoverRef} className={popoverStyle} role='dialog' aria-label='Escolher ícone'>
      <div className={searchWrapStyle}>
        <input
          ref={searchRef}
          className={searchStyle}
          placeholder='Buscar ícone'
          value={query}
          onChange={event => {
            setQuery(event.target.value);
          }}
        />
      </div>

      {results.length === 0 ? (
        <span className={emptyStyle}>Nenhum ícone com esse nome.</span>
      ) : (
        <div className={listStyle}>
          {results.map(icon => (
            <button
              key={icon.value}
              type='button'
              title={icon.value}
              aria-pressed={icon.value === value}
              className={`${optionStyle} ${icon.value === value ? optionSelectedStyle : ''}`}
              onClick={() => {
                onSelect(icon.value);
                onClose();
              }}
            >
              <IconGlyph value={icon.value} size={20} color={color} />
              <span className={optionLabelStyle}>{icon.glyph}</span>
            </button>
          ))}
        </div>
      )}

      <span className={footerStyle}>
        {results.length} de {total} ícones
      </span>
    </div>
  );
};
